/**
 * 📲 SmartBoutik - Composant d'Installation PWA
 * Affiche un prompt pour installer l'application et gérer les mises à jour
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Badge } from 'react-bootstrap';
import { 
  Download, 
  Smartphone, 
  RefreshCw, 
  X, 
  Check,
  Zap,
  WifiOff,
  Bell,
  HardDrive
} from 'lucide-react';
import { 
  canInstall, 
  promptInstall, 
  isAppInstalled,
  applyUpdate,
  isUpdateAvailable 
} from '../registerSW';

/**
 * Bannière d'installation PWA
 */
export const InstallPWABanner = ({ onDismiss }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Vérifier si déjà installé
    if (isAppInstalled()) {
      setInstalled(true);
      return;
    }

    // Écouter l'événement d'installation disponible
    const handleInstallAvailable = () => {
      // Ne pas afficher si déjà refusé récemment
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (dismissed) {
        const dismissedDate = new Date(dismissed);
        const daysSinceDismissed = (Date.now() - dismissedDate) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) return; // Attendre 7 jours
      }
      
      setShowBanner(true);
    };

    // Vérifier immédiatement
    if (canInstall()) {
      handleInstallAvailable();
    }

    // Écouter les futurs événements
    window.addEventListener('pwa-install-available', handleInstallAvailable);
    
    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
    };
  }, []);

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      setInstalled(true);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    setShowBanner(false);
    onDismiss?.();
  };

  if (!showBanner || installed) {
    return null;
  }

  return (
    <Alert 
      variant="primary" 
      className="m-0 rounded-0 border-0"
      style={{ 
        background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
        color: 'white'
      }}
    >
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center">
          <Smartphone size={24} className="me-3" />
          <div>
            <strong>Installer SmartBoutik</strong>
            <div className="small opacity-75">
              Accès rapide et mode hors ligne
            </div>
          </div>
        </div>
        
        <div className="d-flex gap-2">
          <Button 
            variant="light" 
            size="sm"
            onClick={handleInstall}
          >
            <Download size={16} className="me-1" />
            Installer
          </Button>
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={handleDismiss}
          >
            <X size={16} />
          </Button>
        </div>
      </div>
    </Alert>
  );
};

/**
 * Modal d'installation PWA détaillé
 */
export const InstallPWAModal = ({ show, onHide }) => {
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    const success = await promptInstall();
    setInstalling(false);
    
    if (success) {
      onHide();
    }
  };

  const features = [
    { icon: <Zap size={20} />, title: 'Accès rapide', desc: 'Lancez l\'app depuis votre écran d\'accueil' },
    { icon: <WifiOff size={20} />, title: 'Mode hors ligne', desc: 'Continuez à vendre sans Internet' },
    { icon: <Bell size={20} />, title: 'Notifications', desc: 'Recevez des alertes importantes' },
    { icon: <HardDrive size={20} />, title: 'Stockage local', desc: 'Vos données toujours disponibles' }
  ];

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ background: '#ff6b35', color: 'white' }}>
        <Modal.Title className="d-flex align-items-center">
          <Smartphone size={24} className="me-2" />
          Installer SmartBoutik
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <p className="text-muted mb-4">
          Installez SmartBoutik sur votre appareil pour une meilleure expérience :
        </p>
        
        <div className="row g-3 mb-4">
          {features.map((feature, index) => (
            <div key={index} className="col-6">
              <div className="d-flex align-items-start p-2 bg-light rounded">
                <div className="text-primary me-2">{feature.icon}</div>
                <div>
                  <div className="fw-bold small">{feature.title}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {feature.desc}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!canInstall() && (
          <Alert variant="info" className="mb-0">
            <strong>Installation manuelle :</strong>
            <ul className="mb-0 mt-2 small">
              <li><strong>Chrome/Edge :</strong> Menu → "Installer l'application"</li>
              <li><strong>Safari iOS :</strong> Partager → "Sur l'écran d'accueil"</li>
              <li><strong>Firefox :</strong> Menu → "Installer"</li>
            </ul>
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Plus tard
        </Button>
        {canInstall() && (
          <Button 
            variant="primary" 
            onClick={handleInstall}
            disabled={installing}
            style={{ background: '#ff6b35', borderColor: '#ff6b35' }}
          >
            {installing ? (
              <>Installation...</>
            ) : (
              <>
                <Download size={16} className="me-1" />
                Installer maintenant
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Bannière de mise à jour disponible
 */
export const UpdateAvailableBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // Vérifier immédiatement
    if (isUpdateAvailable()) {
      setShowBanner(true);
    }

    // Écouter les futures mises à jour
    const handleUpdateAvailable = () => {
      setShowBanner(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);
    
    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleUpdate = () => {
    setUpdating(true);
    applyUpdate();
    // La page va se recharger automatiquement
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Alert 
      variant="info" 
      className="m-0 rounded-0 border-0"
    >
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center">
          <RefreshCw size={20} className="me-2" />
          <span>
            <strong>Nouvelle version disponible !</strong>
            <span className="d-none d-sm-inline"> Mettez à jour pour les dernières fonctionnalités.</span>
          </span>
        </div>
        
        <Button 
          variant="primary" 
          size="sm"
          onClick={handleUpdate}
          disabled={updating}
        >
          {updating ? (
            <>Mise à jour...</>
          ) : (
            <>
              <RefreshCw size={14} className="me-1" />
              Mettre à jour
            </>
          )}
        </Button>
      </div>
    </Alert>
  );
};

/**
 * Badge d'état PWA (pour le menu/paramètres)
 */
export const PWAStatusBadge = () => {
  const installed = isAppInstalled();
  
  return (
    <Badge 
      bg={installed ? 'success' : 'secondary'}
      className="d-flex align-items-center gap-1"
    >
      {installed ? (
        <>
          <Check size={12} />
          App installée
        </>
      ) : (
        <>
          <Download size={12} />
          Version web
        </>
      )}
    </Badge>
  );
};

/**
 * Bouton d'installation compact
 */
export const InstallButton = ({ variant = 'primary', size = 'md', className = '' }) => {
  const [canShowInstall, setCanShowInstall] = useState(false);

  useEffect(() => {
    if (isAppInstalled()) return;
    
    setCanShowInstall(canInstall());
    
    const handleInstallAvailable = () => setCanShowInstall(true);
    window.addEventListener('pwa-install-available', handleInstallAvailable);
    
    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
    };
  }, []);

  const handleClick = async () => {
    await promptInstall();
  };

  if (!canShowInstall || isAppInstalled()) {
    return null;
  }

  return (
    <Button 
      variant={variant} 
      size={size}
      onClick={handleClick}
      className={className}
    >
      <Download size={16} className="me-1" />
      Installer l'app
    </Button>
  );
};

export default {
  InstallPWABanner,
  InstallPWAModal,
  UpdateAvailableBanner,
  PWAStatusBadge,
  InstallButton
};
